import { useEffect } from "react";
import FilterProps from "./FilterProps";
import { groupingNameMap } from "../../../constants/filterGrouping";
import IconButton from "../../UI/IconButton";
import Toggle from "../../UI/Toggle";
import { AND, OR } from "../../../constants/customFilterType";
import Checkbox from "../../UI/Checkbox";
import { ageOperators } from "../../../constants/customFilterDataType";
import * as filterOperators from "../../../constants/filterOperator";
import AgeValidateResponse, { defaultValue } from "../../../utils/validation/reportFilters/AgeValidateResponse";
import ValidatedSelect from "../../UI/ValidatedSelect";
import ValidatedInput from "../../UI/ValidatedInput";
import ErrorMessages from "../../UI/ErrorMessages";
import { format } from "date-fns";

const AgeFilter = (props: FilterProps) => {
    const validation = props.validation as AgeValidateResponse || defaultValue();

    useEffect(() => {
        if (props.showEndConjunction) {
            handleOnEndConjunctionChange(props.endConjunction);
        } else {
            handleOnEndConjunctionChange(undefined);
        }
    }, [props.showEndConjunction, props.endConjunction]);

    const getParsedValue = (): { from: string, to: string, value: string, asOfDate: string } => {
        const defaultValues = { from: '', to: '', value: '', asOfDate: format(new Date(), 'yyyy-MM-dd') };
        
        if (!props.value) return defaultValues;

        if (props.value.trim().startsWith('{')) {
            try {
                const valueJson = JSON.parse(props.value);
                return {
                    from: valueJson.from || '',
                    to: valueJson.to || '',
                    value: valueJson.value || '',
                    asOfDate: valueJson.asOfDate || defaultValues.asOfDate
                };
            } catch (e) {
                return defaultValues;
            }
        }

        return { ...defaultValues, value: props.value };
    };

    useEffect(() => {
        if (props.onValueChange && (!props.value || !props.value.trim().startsWith('{'))) {
            props.onValueChange(props.id, JSON.stringify(getParsedValue()));
        }
    }, [props.value]);

    useEffect(() => {
        if (props.showStartConjunction) {
            handleOnStartConjunctionChange(props.startConjunction);
        } else {
            handleOnStartConjunctionChange(undefined);
        }
    }, [props.showStartConjunction, props.startConjunction]);

    const handleOnStartConjunctionChange = (conjunction?: string) => {
        props.onStartConjunctionChange(props.id, conjunction);
    };

    const handleOnEndConjunctionChange = (conjunction?: string) => {
        props.onEndConjunctionChange(props.id, conjunction);
    };

    const handleOnValueChange = (value: string, type: 'from' | 'to' | 'value' | 'asOfDate') => {
        const newValue = { ...getParsedValue(), [type]: value };
        props.onValueChange!(props.id, JSON.stringify(newValue));
    };

    const handleOnDelete = () => {
        props.onDelete(props.id);
    };

    const handleOnSelectedChanged = (selected: boolean) => {
        props.onSelectedChange!(props.id, selected);
    };

    const handleOnOperatorChange = (operator: string) => {
        props.onOperatorChange!(props.id, operator);
        const current = getParsedValue();
        props.onValueChange!(props.id, JSON.stringify(current));
    };
    const parsedValue = getParsedValue();

    const getInputs = () => {
        if (!props.operator) {
            return null;
        }

        switch (props.operator) {
            case filterOperators.EQUALS:
            case filterOperators.NOT_EQUALS:
            case filterOperators.GREATER_THAN:
            case filterOperators.GREATER_THAN_OR_EQUAL_TO:
            case filterOperators.LESS_THAN:
            case filterOperators.LESS_THEN_OR_EQUAL_TO:
                return <>
                    <ValidatedInput id={`filter-date-equals-${props.id}`}
                        messages={validation.value}
                        value={parsedValue.value}
                        label="Value"
                        type="number"
                        onChange={(v) => handleOnValueChange(v, 'value')} />
                </>;
            case filterOperators.BETWEEN:
                return <div className="row align-items-center justify-content-between">
                    <div className="column-auto">
                        <ValidatedInput id={`date-filter-from-${props.id}`}
                            messages={validation.from}
                            value={parsedValue.from}
                            label="From"
                            onChange={v => handleOnValueChange(v, 'from')}
                            type="number" />
                    </div>
                    <div className="column-auto margin-bottom-5">
                        and
                    </div>
                    <div className="column-auto">
                        <ValidatedInput id={`date-filter-to-${props.id}`}
                            messages={validation.to}
                            value={parsedValue.to}
                            label="To"
                            onChange={v => handleOnValueChange(v, 'to')}
                            type="number" />
                    </div>
                </div>
            default:
                console.warn('Operator is unknown for date filter', props.operator);
                break;
        }

        return null;
    };

    return <>
        {props.showStartConjunction && <Toggle id={`conjunction-${props.id}`}
            option1={{ label: 'And', value: AND }}
            option2={{ label: 'Or', value: OR }}
            onChange={handleOnStartConjunctionChange}
            value={props.startConjunction} />}
        <ErrorMessages messages={validation.default} />
        <div className="row align-items-center justify-content-between">
            <div className="column-auto">
                <Checkbox id={`filter-selected-${props.id}`}
                    checked={props.selected}
                    label="Select to Group"
                    containerClassName="unboxed margin-top-0"
                    onChange={handleOnSelectedChanged} />
            </div>
            <div className="column-auto">
                <IconButton id={`delete-filter-${props.id}`}
                    icon={<i className="ri-delete-bin-line"></i>}
                    onClick={handleOnDelete}
                    className="small flat" />
            </div>
        </div>
        <div className="row padding-3">
            <div className="column-auto text-bold">
                {groupingNameMap[props.grouping]}
            </div>
        </div>
        <div className="row padding-3">
            <div className="column-auto">
                {props.name}
            </div>
        </div>
        <div className="row align-items-center padding-3 filter-config-row">
            <div className="column-3 filter-operator-column">
                <ValidatedSelect id={`filter-operation-${props.id}`}
                    messages={validation.operator}
                    value={props.operator}
                    onChange={handleOnOperatorChange}
                    label="Operator">
                    <option value="">Select an Operator</option>
                    {ageOperators.map((m, i) => {
                        return <option key={i} value={m}>{filterOperators.operatorLabelMap[m]}</option>
                    })}
                </ValidatedSelect>
            </div>
            <div className="column-auto filter-value-column">
                {getInputs()}
            </div>
        </div>
        <div className="row margin-top-3 padding-3">
            <div className="column-3">
                <ValidatedInput id={`as-of-date-${props.id}`}
                    messages={validation.asOfDate}
                    label="As of Date"
                    value={parsedValue.asOfDate}
                    type="date"
                    onChange={(v) => handleOnValueChange(v, 'asOfDate')} />
            </div>
        </div>
        {props.showEndConjunction && <Toggle id={`conjunction-${props.id}`}
            option1={{ label: 'And', value: AND }}
            option2={{ label: 'Or', value: OR }}
            onChange={handleOnEndConjunctionChange}
            value={props.endConjunction} />}
    </>
};

export default AgeFilter;
